import { Box, Button, CircularProgress, LinearProgress, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS as en, esES as es, ptBR } from "@mui/x-data-grid/locales";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import MyModal from "../MyModal";
import { IVwLoyalty, loyaltyService } from "../../services/loyaltyService";

interface IModalBatchParticipants {
  jobId: string;
  jobLabel?: string;
  modalChange: (open: boolean) => void;
}

const headerSx = {
  "& .header-style": {
    backgroundColor: "var(--gray-100)",
    minHeight: "4rem",
  },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "700 !important" },
};

export default function ModalBatchParticipants({
  jobId,
  jobLabel,
  modalChange,
}: IModalBatchParticipants) {
  const { t, i18n } = useTranslation();
  const {
    tokenHeaders,
    country: { countryCode },
  } = useContext(AuthContext);

  const languages: { [key: string]: any } = { ptbr: ptBR, en, es };
  const currentLocale = languages[i18n.language] ?? en;
  const localeText =
    currentLocale.components.MuiDataGrid.defaultProps.localeText;

  const [rows, setRows] = useState<IVwLoyalty[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const dateLocale =
    i18n.language === "ptbr"
      ? "pt-BR"
      : i18n.language === "es"
        ? "es"
        : "en-US";

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      try {
        const headers = {
          ...tokenHeaders,
          headers: { ...tokenHeaders.headers, countryCode },
        };
        const params: Record<string, any> = { page, size: pageSize };
        if (search) params.search = search;
        const { data } = await loyaltyService.getBatchParticipants(jobId, params, headers);
        setRows(data.content);
        setTotal(data.totalElements);
      } catch {
        setRows([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, jobId, search]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const columns: GridColDef[] = [
    {
      field: "customerEmail",
      headerName: t("loyalty.groups.participants.columns.email"),
      flex: 2,
      minWidth: 200,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "loyaltyPlan",
      headerName: t("loyalty.groups.participants.columns.category"),
      flex: 1,
      minWidth: 120,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: string) => t(`loyalty.plans.${value}`, value),
    },
    {
      field: "active",
      headerName: t("loyalty.groups.participants.columns.status"),
      flex: 1,
      minWidth: 100,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: boolean) =>
        value ? t("loyalty.status.active") : t("loyalty.status.inactive"),
    },
    {
      field: "updatedAt",
      headerName: t("loyalty.groups.participants.columns.updatedAt"),
      flex: 1,
      minWidth: 180,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString(dateLocale) : "-",
    },
  ];

  return (
    <MyModal
      title={t("loyalty.groups.participants.title", { groupName: jobLabel ?? "" })}
      modalChange={modalChange}
      width="90%"
      maxWidth="900px"
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("loyalty.groups.participants.searchLabel")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            placeholder={t("loyalty.groups.searchLabel")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ flex: 1, maxWidth: 360 }}
          />
          <Button variant="contained" onClick={handleSearch}>
            {t("loyalty.groups.searchButton")}
          </Button>
          <Button sx={{ color: "#00AFF2" }} variant="outlined" onClick={handleClear}>
            {t("loyalty.groups.cleanButton")}
          </Button>
        </Box>
      </Box>

      {isLoading && !rows.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={headerSx}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={total}
            loading={isLoading}
            getRowId={(row) => row.id}
            pageSizeOptions={[25, 50, 75, 100]}
            pagination
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            localeText={localeText}
            slots={{ loadingOverlay: LinearProgress as any }}
            autoHeight
            disableColumnFilter
            disableRowSelectionOnClick
            sx={{ "& .MuiDataGrid-row": { cursor: "default" } }}
          />
        </Box>
      )}
    </MyModal>
  );
}
